import React, { useMemo } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { isEmpty } from 'lodash-es'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'

// Libs
import { useCategoryMenu } from '@/hooks/useCategoryMenu'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'

// Styles
import styles from './style.module.scss'
import 'react-loading-skeleton/dist/skeleton.css'

// Variables
const cx = classnames.bind(styles)

// 麵包屑只渲染分類樹的前三層
const MAX_RENDER_TIER = 3
const SEPARATOR = '>'

interface IProps {
  categoryId?: number
  categoryName?: string
  // 有值時在分類路徑前多一層，點了回商城首頁
  rootLabel?: string
  // 不是分類的頁面（例：主題企劃）直接給固定路徑，有 onClick 的才可點；給了就不看分類樹
  items?: { name: string; onClick?: () => void }[]
}

interface ICategoryNode {
  id: number
  name: string
  children: ICategoryNode[] | null
}

interface IBreadcrumbItem {
  id: number | null
  name: string
  isClickable: boolean
  onClick?: () => void
}

// DFS 找到 categoryId 節點，回傳 root 到該節點走過的路徑（跳過 id < 0 的節點，也就是熱門）
const findCategoryPath = (nodes: ICategoryNode[] | null | undefined, categoryId: number, path: ICategoryNode[] = []): ICategoryNode[] | null => {
  if (!Array.isArray(nodes)) return null

  for (const node of nodes) {
    if (isEmpty(node) || node.id < 0) continue

    const currentPath = [...path, node]

    if (node.id === categoryId) return currentPath

    const foundPath = findCategoryPath(node.children, categoryId, currentPath)

    if (foundPath) return foundPath
  }

  return null
}

// 熱門這類特殊分類（id < 0）只掛在分類樹第一層，不走一般路徑搜尋，直接在第一層找
const findSpecialCategory = (nodes: ICategoryNode[] | null | undefined, categoryId: number): ICategoryNode | null =>
  (Array.isArray(nodes) && nodes.find((node) => !isEmpty(node) && node.id === categoryId)) || null

interface ISchemaBreadcrumbItem {
  name: string
  position: number
  url: string
}

/**
 * 結構化資料（JSON-LD BreadcrumbList）用：商城 > 分類路徑，跟畫面上的麵包屑同一套規則（最多三層）。
 * 分類樹查不到時退回 categoryName（商品 API 回傳的分類名），兩者都沒有就只剩商城。
 */
export const getCategoryBreadcrumbSchemaList = ({
  categoryTree,
  categoryId,
  categoryName,
  mallLabel,
  mallUrl,
}: {
  categoryTree: ICategoryNode[] | null | undefined
  categoryId?: number
  categoryName?: string
  mallLabel: string
  mallUrl: string
}): ISchemaBreadcrumbItem[] => {
  let categoryPath: ICategoryNode[] | null = null

  try {
    if (categoryId && categoryId > 0) categoryPath = findCategoryPath(categoryTree, categoryId)
    if (categoryId && categoryId < 0) {
      const specialCategory = findSpecialCategory(categoryTree, categoryId)
      categoryPath = specialCategory ? [specialCategory] : null
    }
  } catch (error) {
    categoryPath = null
  }

  const nodes: { id: number; name: string }[] = categoryPath
    ? categoryPath.slice(0, MAX_RENDER_TIER)
    : categoryId && categoryId > 0 && categoryName
    ? [{ id: categoryId, name: categoryName }]
    : []

  return [
    { name: mallLabel, position: 1, url: mallUrl },
    ...nodes.map((node, index) => ({ name: node.name, position: index + 2, url: `${mallUrl}?category_id=${node.id}` })),
  ]
}

const Breadcrumb = (props: IProps) => {
  const { categoryId, categoryName, rootLabel, items } = props
  const router = useRouter()

  const { data: categoryMenuData, isLoading } = useCategoryMenu({ enabled: !items })

  const breadcrumbItems = useMemo<IBreadcrumbItem[]>(() => {
    if (items) return items.map((item) => ({ id: null, name: item.name, isClickable: !!item.onClick, onClick: item.onClick }))

    if (categoryId && categoryId < 0) {
      const specialCategory = findSpecialCategory(categoryMenuData?.root?.children, categoryId)

      return specialCategory ? [{ id: specialCategory.id, name: specialCategory.name, isClickable: true }] : []
    }

    if (!categoryId) {
      return categoryName ? [{ id: null, name: categoryName, isClickable: false }] : []
    }

    let categoryPath = null

    // 分類樹結構異常時走 fallback，確保麵包屑不會導致頁面崩潰
    try {
      categoryPath = findCategoryPath(categoryMenuData?.root?.children, categoryId)
    } catch (error) {
      categoryPath = null
    }

    // 在分類樹查不到路徑時，顯示商品 API 回傳的 category_name
    if (isEmpty(categoryPath)) {
      return categoryName ? [{ id: null, name: categoryName, isClickable: false }] : []
    }

    return (categoryPath as ICategoryNode[]).slice(0, MAX_RENDER_TIER).map((node) => ({
      id: node.id,
      name: node.name,
      isClickable: true,
    }))
  }, [items, categoryMenuData, categoryId, categoryName])

  const handleClickItem = (item: IBreadcrumbItem) => {
    if (item.onClick) return item.onClick()

    if (!item.isClickable || item.id === null) return

    handleRoutePushWithQuery({ router, pathname: ROUTE_PATH.MALL, query: { category_id: item.id } })
  }

  const handleClickRoot = () => {
    handleRoutePushWithQuery({ router, pathname: ROUTE_PATH.MALL })
  }

  // 分類樹載入期間顯示單行 skeleton，避免先渲染 fallback 再展開成完整路徑造成畫面閃爍與 CLS
  if (isLoading) {
    return (
      <div className={cx('breadcrumb')}>
        <SkeletonTheme baseColor='#EAEAEA' highlightColor='#F5F5F5' duration={2}>
          <Skeleton width={200} height={18} borderRadius={100} />
        </SkeletonTheme>
      </div>
    )
  }

  if (isEmpty(breadcrumbItems)) return null

  return (
    <div className={cx('breadcrumb')}>
      {rootLabel && (
        <>
          <div onClick={handleClickRoot} className={cx('breadcrumb__item', 'breadcrumb__item--clickable')}>
            {rootLabel}
          </div>
          <div className={cx('breadcrumb__separator')}>{SEPARATOR}</div>
        </>
      )}
      {breadcrumbItems.map((item, index) => (
        <React.Fragment key={`${item.id}-${index}`}>
          {index > 0 && <div className={cx('breadcrumb__separator')}>{SEPARATOR}</div>}
          <div
            onClick={() => handleClickItem(item)}
            className={cx('breadcrumb__item', `breadcrumb__item--tier-${index + 1}`, { 'breadcrumb__item--clickable': item.isClickable })}
          >
            {item.name}
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

export default Breadcrumb
